import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import loanFileService from "../../../services/loanFileService";
import DetailPageLayout from "../../../components/detail/DetailPageLayout";
import DetailInfoSidebar from "../../../components/detail/DetailInfoSidebar";
import ConfirmDeleteModal from "../../../components/detail/ConfirmDeleteModal";
import { Box, Button, Modal, Tab, Tabs, TextField, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useForm } from "react-hook-form";
import { instanceToken, BACKEND_URL } from "../../../utils/constant";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 350,
  bgcolor: "background.paper",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  boxShadow: 24,
  p: 3,
};

const LoanFile = ({ homeAlert }) => {
  const { insurance_id } = useParams();
  const [loanFiles, setLoanFiles] = useState(null);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openRemove, setOpenRemove] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [insuranceId, setInsuranceId] = useState("");
  const [fileId, setFileId] = useState("");
  const { register, handleSubmit, setValue } = useForm();

  const token = localStorage.getItem("cblife_claimForm_token");
  const parsedToken = token ? JSON.parse(token) : null;
  const jwtToken = parsedToken?.jwttoken;

  const fetchLoanFiles = async () => {
    try {
      const res = await loanFileService.getLoanFile(
        instanceToken.token,
        insurance_id
      );
      setLoanFiles(res.data);
      setFilteredFiles(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLoanFiles();
  }, [insurance_id]);

  const filterFiles = (files, tabIndex) => {
    if (tabIndex === 0) return files;
    if (tabIndex === 1) {
      return files.filter((file) => file.type === "application/pdf");
    }
    if (tabIndex === 2) {
      return files.filter(
        (file) =>
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
    }
    if (tabIndex === 3) {
      return files.filter((file) => file.type === "image/jpeg");
    }
    return files;
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (loanFiles) {
      setFilteredFiles(filterFiles(loanFiles, newValue));
    }
  };

  const handleOpenRemove = (file) => {
    setFileToEdit(file);
    setOpenRemove(true);
  };

  const handleRemove = async () => {
    try {
      await loanFileService.deleteLoanFile(jwtToken, fileToEdit?.id);
      homeAlert("Loan File has been removed.", false);
      setOpenRemove(false);
      setFileToEdit(null);
      fetchLoanFiles();
    } catch (error) {
      console.log(error);
      homeAlert("Error on server!", true);
    }
  };

  const handleOpenEditModal = (file) => {
    setFileToEdit(file);
    setInsuranceId(file.insurance_id);
    setFileId(file.id);
    setValue("insuranceId", file.insurance_id);
    setValue("id", file.id);
    setOpenEditModal(true);
  };

  const handleEdit = async (data) => {
    const formData = new FormData();
    Array.from(data.files).forEach((file) => {
      formData.append("files", file);
    });
    formData.append("insuranceId", data.insuranceId);
    formData.append("id", data.id);

    try {
      await loanFileService.updateLoanFile(jwtToken, fileToEdit?.id, formData);
      homeAlert("Loan File has been updated.", false);
      setOpenEditModal(false);
      setFileToEdit(null);
      fetchLoanFiles();
    } catch (error) {
      console.log(error);
      homeAlert("Error on server!", true);
    }
  };

  return (
    <DetailPageLayout
      title="Loan File Details"
      subtitle="View and manage loan files for this insurance ID"
      breadcrumbs={[
        { label: "Loan Files", path: "/loanFiles" },
        { label: `Loan Files (ID - ${insurance_id})` },
      ]}
      listPath="/loanFiles"
      loading={!loanFiles}
    >
      <Box className="detail-tabs" sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="All" />
          <Tab label="PDFs" />
          <Tab label="Word Documents" />
          <Tab label="Images" />
        </Tabs>
      </Box>

      <div className="detail-file-body">
        <div className="detail-file-list">
          {filteredFiles.map((row) => (
            <div key={row.id} className="detail-file-card">
              <div className="detail-content-section">
                <div className="detail-content-section-head">
                  <span className="detail-section-icon">
                    <ArticleOutlinedIcon fontSize="small" />
                  </span>
                  <h3 className="detail-section-title">{row.name}</h3>
                </div>
                <div className="detail-section-body" style={{ paddingLeft: 42 }}>
                  <div>Type: {row.type}</div>
                </div>
              </div>
              <div className="detail-file-card-actions">
                <Button variant="contained">
                  <a
                    href={`${BACKEND_URL}api/download?${new URL(row.file_path).searchParams.toString()}`}
                    download={row.name}
                    className="downloadTag"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download file
                  </a>
                </Button>
                <Button variant="contained" onClick={() => handleOpenEditModal(row)}>
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleOpenRemove(row)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DetailInfoSidebar
          infoTitle="File Information"
          infoItems={[
            { label: "Insurance ID", value: insurance_id, icon: "id" },
            {
              label: "Total Files",
              value: String(loanFiles?.length || 0),
              icon: "info",
            },
            {
              label: "Showing",
              value: String(filteredFiles.length),
              icon: "info",
            },
          ]}
        />
      </div>

      <ConfirmDeleteModal
        open={openRemove}
        onClose={() => {
          setOpenRemove(false);
          setFileToEdit(null);
        }}
        onConfirm={handleRemove}
        message="Are you sure you want to remove this file?"
      />

      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2">
            Edit Loan File
          </Typography>
          <form onSubmit={handleSubmit(handleEdit)}>
            <TextField
              fullWidth
              label="Insurance ID"
              {...register("insuranceId")}
              value={insuranceId}
              onChange={(e) => setInsuranceId(e.target.value)}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="File ID"
              {...register("id")}
              value={fileId}
              sx={{ mt: 2 }}
              disabled
            />
            <input
              type="file"
              {...register("files")}
              style={{ marginTop: "1rem", display: "block" }}
            />
            <Box sx={{ textAlign: "right", mt: 2 }}>
              <Button color="inherit" onClick={() => setOpenEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={{ ml: 1 }}>
                Save
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </DetailPageLayout>
  );
};

export default LoanFile;
